import { cn } from '@h2hspark/ui';
import * as React from 'react';

import {
  CHECKOUT_SECTION_CARD,
  ONE_TICKET_WHICHEVER_YOU_CHOOSE,
  TICKET_CHOICE_GROUP_LABEL,
  TICKET_CHOICE_HEADING,
  ticketChoiceLabel,
} from './constants';
import type { TicketChoice } from './types';

/**
 * **Which ticket the member is buying** — the control that closes the hole this slice shipped
 * with.
 *
 * ## What it is for
 *
 * `EVT-017` lets an organizer define several ticket types at several prices, and until this
 * component existed no member could pick one: every order was priced from the event's first type
 * and checkout's request schema refused a `ticketTypeId` outright. An event offering Early bird /
 * General / VIP charged everyone the first tier.
 *
 * ## It is not drawn for one ticket, and never for a free event
 *
 * A group of one radio is a decision the member does not have, drawn to look like one they do —
 * so a single-type event renders nothing here and its checkout is exactly the screen it was
 * before. A free event carries no ticket types at all (`EVT-017` AC-2), so `ticketChoices` is
 * empty and the same absence answers both.
 *
 * ## No price is formatted here, and no seat count is drawn
 *
 * `choice.price` is the server's own rendering, produced by `formatCents` in integer arithmetic,
 * and this file concatenates it into a label and nothing more — the rule `reserveActionLabel` and
 * `OrderItemisation` both follow, and what makes these bytes assertable on a machine whose Node
 * version differs from CI's.
 *
 * There is deliberately **no "3 left" on any row.** Capacity is per event (`EVT-020`), a hold
 * holds a seat rather than a tier, and a per-type count is the first field of the hold-back, pool
 * and quota `EVT-019` AC-3 forbids. Choosing VIP takes the same seat off the same number as
 * choosing General.
 *
 * ## Markup, and `accessibility#wcag-aa`
 *
 * A real `<fieldset>` of real `<input type="radio">`s, named by the visible heading through
 * `aria-labelledby` rather than by a duplicated string. That is what makes the group arrow-key
 * navigable and announced as "Choose your ticket, General admission $30.00, 2 of 3" without this
 * file implementing roving focus — the same reason `EVT-017`'s own reorder control is a pair of
 * buttons rather than a drag handle.
 *
 * The rows are the prototype's selectable cards (`Web App.dc.html` §3.1, the `resFormats` rows):
 * `1.5px` hairline over `--surface-card`, and the chosen row taking the brand border **and** the
 * `--purple-50` fill — two signals rather than one, so the selection survives a reader who cannot
 * separate purple from grey. The radio is still a radio; the card is what it looks like.
 */
export function TicketTypeChoicePanel({
  choices,
  selectedTicketTypeId,
  onChoose,
  disabled = false,
}: {
  /** In the organizer's own order, unsorted (`EVT-017` AC-4). Empty on a free event. */
  readonly choices: ReadonlyArray<TicketChoice>;
  /**
   * The ticket this checkout is priced for — read off the **order**, not held here.
   *
   * `null` only for a free event's order, which names no type. Keeping the selection on the
   * server's answer rather than in component state is what stops the summary and the selector
   * disagreeing about what is being bought while a request is in flight.
   */
  readonly selectedTicketTypeId: string | null;
  readonly onChoose: (ticketTypeId: string) => void;
  /** True while a re-price is in flight, so two rapid taps cannot queue two open-checkout calls. */
  readonly disabled?: boolean;
}): React.ReactElement | null {
  const headingId = React.useId();
  // One choice is not a choice. See the header: a group of one radio offers a decision that does
  // not exist, and a free event's empty list is the same absence arriving from `EVT-017` AC-2.
  if (choices.length < 2) return null;

  return (
    <section className={`${CHECKOUT_SECTION_CARD} space-y-2.5`} data-testid="ticket-type-choice">
      <h2 id={headingId} className="font-brand text-sm font-bold text-foreground">
        {TICKET_CHOICE_HEADING}
      </h2>
      <fieldset
        className="space-y-2"
        aria-labelledby={headingId}
        aria-label={TICKET_CHOICE_GROUP_LABEL}
      >
        {choices.map((choice) => {
          const selected = choice.ticketTypeId === selectedTicketTypeId;
          return (
            <label
              key={choice.ticketTypeId}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md border-[1.5px] p-3.5 text-sm transition-colors',
                selected
                  ? 'border-brand bg-brand-50'
                  : 'border-border bg-card hover:border-brand/40',
              )}
            >
              <input
                type="radio"
                name="ticketTypeId"
                value={choice.ticketTypeId}
                /* The name and the price as one announced string. Without it a reader gets the
                   two spans concatenated by whatever the browser decides to put between them,
                   which is not a thing a test can assert and not a thing two browsers agree on. */
                aria-label={ticketChoiceLabel(choice.name, choice.price)}
                checked={selected}
                disabled={disabled}
                onChange={() => {
                  onChoose(choice.ticketTypeId);
                }}
                className="h-4 w-4 accent-brand"
              />
              <span className="flex-1 font-brand font-bold text-foreground">{choice.name}</span>
              {/* The server's rendering, concatenated and never composed. */}
              <span className="font-brand font-bold text-foreground">{choice.price}</span>
            </label>
          );
        })}
      </fieldset>
      {/* `MEM-020` AC-1's fixed quantity, said where somebody who has just chosen a tier would
          otherwise go looking for a stepper. A sentence, not a control — `MEM-TKT-001`'s own
          correction about the organizer surface's "Max per person" select. */}
      <p className="text-xs text-muted-foreground">{ONE_TICKET_WHICHEVER_YOU_CHOOSE}</p>
    </section>
  );
}
